import { z } from 'zod';
// Shared types for Appointment Visits (multi-service visit)
export const VisitAvailabilityRequestSchema = z.object({
    tenant_id: z.string().uuid(),
    appointment_type_ids: z.array(z.string().uuid()).min(1), // ordered sequence
    team_member_id: z.union([z.string().uuid(), z.literal('any')]),
    date_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // YYYY-MM-DD
    date_end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // YYYY-MM-DD
});
export const VisitAvailabilitySlotSchema = z.object({
    time: z.string(), // HH:MM in local business timezone
    available: z.boolean(),
    teamMembers: z.array(z.object({ id: z.string().uuid(), name: z.string() })).optional(),
    reasons: z.array(z.string()).optional(), // optional reasons for rejection when available=false
});
export const VisitAvailabilityResponseSchema = z.object({
    success: z.literal(true),
    available_slots: z.record(z.string(), z.array(VisitAvailabilitySlotSchema)),
    appointment_details: z.object({
        appointment_types: z.array(z.object({ id: z.string().uuid(), title: z.string(), duration: z.number() })),
        total_duration: z.number(),
    }),
});
export const VisitAvailabilityErrorSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
export const CreateVisitBookingPublicSchema = z.object({
    tenant_id: z.string().uuid(),
    appointment_type_ids: z.array(z.string().uuid()).min(1),
    team_member_id: z.string().uuid().nullable().optional(), // null/undefined => auto-assign
    customer_first_name: z.string().min(1).max(100),
    customer_last_name: z.string().min(1).max(100),
    customer_email: z.string().email(),
    customer_phone: z.string().optional(),
    customer_marketing_opt_ins: z.object({ email: z.boolean(), whatsapp: z.boolean() }).optional().default({ email: false, whatsapp: false }),
    start_time: z.string().datetime(), // ISO UTC start of first service
    notes: z.string().nullable().optional(),
});
export const CreateVisitBookingPublicResponseSchema = z.object({
    success: z.literal(true),
    visit_id: z.string().uuid().optional(),
    appointment_ids: z.array(z.string().uuid()),
    team_member_id: z.string().uuid(),
    appointment_details: z.object({
        start_time: z.string(),
        end_time: z.string(),
        total_duration: z.number(),
        appointment_types: z.array(z.object({ id: z.string().uuid(), title: z.string() })),
        customer_name: z.string(),
        status: z.string(),
    }),
});
export const CreateVisitBookingPublicErrorSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
export const CreateVisitBookingAdminSchema = z.object({
    tenant_id: z.string().uuid(),
    appointment_type_ids: z.array(z.string().uuid()).min(1),
    team_member_id: z.string().uuid().optional(),
    team_member_ids: z.array(z.string().uuid()).min(1).optional(),
    customer_id: z.string().uuid().optional(),
    // Alternatively allow full customer payload similar to single admin create flow (future)
    start_time: z.string().datetime(),
    notes: z.string().nullable().optional(),
    notify_customer: z.boolean().optional(),
}).superRefine((data, ctx) => {
    if (data.team_member_ids && data.team_member_ids.length > 0) {
        if (data.team_member_ids.length !== data.appointment_type_ids.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['team_member_ids'],
                message: 'team_member_ids length must match appointment_type_ids length',
            });
        }
    }
    else if (!data.team_member_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['team_member_id'],
            message: 'team_member_id is required when team_member_ids are not provided',
        });
    }
});
export const CreateVisitBookingAdminResponseSchema = z.object({
    success: z.literal(true),
    visit_id: z.string().uuid().optional(),
    appointment_ids: z.array(z.string().uuid()),
});
